import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SeoMeta from '../Shared/SeoMeta';
import styles from './PublicDiscoveryPage.module.css';

interface DiscoverySection {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
}

interface DiscoveryFaq {
  question: string;
  answer: string;
}

export interface PublicDiscoveryPageData {
  path: string;
  metaTitle: string;
  eyebrow: string;
  heroTitle: string;
  description: string;
  lead: string;
  cardTitle: string;
  cardDescription: string;
  highlights: string[];
  sections: DiscoverySection[];
  faq: DiscoveryFaq[];
}

export const PUBLIC_DISCOVERY_PAGES: PublicDiscoveryPageData[] = [
  {
    path: '/text-based-arena-rpg',
    metaTitle: 'Text-Based Arena RPG | Njordheim Arena',
    eyebrow: 'Text-Based Arena RPG',
    heroTitle: 'Looking for a text-based arena RPG with real progression?',
    description: 'Njordheim Arena is a browser-based text-based arena RPG focused on readable combat logs, mercenary progression, guild rivalry, and seasonal tournaments.',
    lead: 'Njordheim Arena is built for players who want the clarity of a text-based game without giving up long-term strategy. Every battle is readable, every build decision matters, and every season gives your mercenary company something to chase.',
    cardTitle: 'Text-Based Arena RPG',
    cardDescription: 'See why Njordheim fits players searching for a readable, browser-based arena RPG with long-term competition.',
    highlights: ['Readable combat logs', 'Turn-based arena battles', 'Guild and tournament rivalry'],
    sections: [
      {
        heading: 'Why it fits the genre',
        paragraphs: [
          'Njordheim is structured around text-based combat that tells you what happened and why. You can follow every attack, dodge, critical hit, rune interaction, and tactical swing without needing to react in milliseconds.',
          'That makes it a strong fit for players who like strategy, planning, and progression more than flashy action loops.'
        ]
      },
      {
        heading: 'What you do between fights',
        paragraphs: [
          'The arena is only part of the loop. Between battles you build better mercenaries through equipment, crafting, beasts, professions, rankings, and long-term roster choices.'
        ],
        bullets: [
          'Tune equipment, runes, and stats for different matchups',
          'Fight beasts and rivals to improve your roster',
          'Use the economy and crafting systems to support progression'
        ]
      },
      {
        heading: 'Why the rivalry matters',
        paragraphs: [
          'Seasonal ladders, guild competition, and tournaments give the text combat real stakes. You are not only reading a fight log; you are pushing a mercenary company higher than other players trying to do the same.'
        ]
      }
    ],
    faq: [
      {
        question: 'Does Njordheim play like a classic text game?',
        answer: 'Yes. The combat is readable and log-driven, but the surrounding systems are modern and persistent, with guilds, rankings, items, and seasonal competition.'
      },
      {
        question: 'Is it only arena combat?',
        answer: 'Arena combat is the core identity, but the game also includes beasts, crafting, economy systems, professions, and broader mercenary progression.'
      }
    ]
  },
  {
    path: '/mercenary-browser-game',
    metaTitle: 'Mercenary Browser Game | Njordheim Arena',
    eyebrow: 'Mercenary Browser Game',
    heroTitle: 'A mercenary browser game built around growth, loadouts, and rivalry',
    description: 'Njordheim Arena is a mercenary browser game where you build, equip, and direct fighters through arena battles, beasts, crafting, guild conflict, and seasonal competition.',
    lead: 'If you want a mercenary game that runs in your browser and gives you more to do than click through missions, Njordheim gives you a company to build. Your mercenaries grow through battle results, gear choices, professions, and the pressure of fighting stronger opponents over time.',
    cardTitle: 'Mercenary Browser Game',
    cardDescription: 'Explore how mercenary growth, gearing, beasts, and long-term roster building work inside Njordheim.',
    highlights: ['Roster growth over time', 'Equipment and rune planning', 'Browser-based access with no client install'],
    sections: [
      {
        heading: 'You build mercenaries, not disposable units',
        paragraphs: [
          'Each mercenary becomes a long-term project. You shape a character through gear, race, combat results, resources, and support systems instead of replacing them every few minutes.',
          'That makes every win, loss, and upgrade part of a larger company story.'
        ]
      },
      {
        heading: 'Progression has multiple layers',
        paragraphs: [
          'Mercenary progression is not limited to levels. Njordheim lets you improve the company through market decisions, crafting, equipment upgrades, and competitive positioning.'
        ],
        bullets: [
          'Choose better loadouts for specific roles and opponents',
          'Use crafting and trading to improve your options',
          'Push mercenaries into tougher fights for better rewards and status'
        ]
      },
      {
        heading: 'Why browser-first matters',
        paragraphs: [
          'Because the game runs in a browser, it is easy to check in, manage your fighters, and follow battles without downloading a dedicated client. That lowers friction while keeping the deeper strategy intact.'
        ]
      }
    ],
    faq: [
      {
        question: 'Do I control a whole party or one mercenary?',
        answer: 'The game centers on mercenary-driven progression, and that progression extends into a broader company identity through rivalry, guild participation, and supporting systems.'
      },
      {
        question: 'What makes it a mercenary game instead of a generic RPG?',
        answer: 'The focus is on training, equipping, and directing combat-focused mercenaries in a competitive world rather than purely exploring a story map.'
      }
    ]
  },
  {
    path: '/guild-wars-browser-game',
    metaTitle: 'Guild Wars Browser Game | Njordheim Arena',
    eyebrow: 'Guild Wars Browser Game',
    heroTitle: 'Guild rivalry, seasonal pressure, and browser-based competition',
    description: 'Njordheim Arena is a guild wars browser game where mercenary rosters, cooperation, rankings, and seasonal competition matter as much as individual battle strength.',
    lead: 'Some players are not only searching for a browser RPG. They want a guild game where collective pressure matters. Njordheim supports that through guild rivalry, ranking goals, and a world where your mercenary company contributes to something larger than one duel.',
    cardTitle: 'Guild Wars Browser Game',
    cardDescription: 'Learn how guild play, coordinated rivalry, and seasonal stakes add a broader competitive layer beyond solo battles.',
    highlights: ['Guild identity and rivalry', 'Seasonal ranking pressure', 'Mercenary rosters that support team goals'],
    sections: [
      {
        heading: 'Guild play adds a second strategic layer',
        paragraphs: [
          'Njordheim is not only about isolated mercenary improvement. Guild systems give players a reason to coordinate, compare growth, and push for shared standing over longer arcs.'
        ]
      },
      {
        heading: 'Why that matters for competitive players',
        paragraphs: [
          'Guild rivalry changes the meaning of progression. Improving a mercenary is not just personal power; it supports a larger competitive identity in rankings, battles, and seasonal pressure.'
        ],
        bullets: [
          'Grow stronger rosters that help your guild stay relevant',
          'Compete in a world shaped by rival groups, not just solo players',
          'Use long-term progression for more than one-off wins'
        ]
      },
      {
        heading: 'Still playable as a browser game',
        paragraphs: [
          'All of that remains accessible through the browser. The game keeps the barrier to entry low while supporting the kind of persistent rivalry that usually takes heavier clients or much larger time commitments.'
        ]
      }
    ],
    faq: [
      {
        question: 'Do guilds matter in Njordheim?',
        answer: 'Yes. Guild identity, rivalry, and ranking pressure are part of what gives the broader game world lasting competition beyond individual arena results.'
      },
      {
        question: 'Can I still enjoy the game without a guild?',
        answer: 'Yes, but guild play adds a meaningful extra layer for players who want more coordination, rivalry, and long-term social stakes.'
      }
    ]
  }
];

interface PublicDiscoveryPageProps {
  page: PublicDiscoveryPageData;
}

const buildFaqSchema = (page: PublicDiscoveryPageData) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: page.faq.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
});

const buildWebPageSchema = (page: PublicDiscoveryPageData) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: page.heroTitle,
  description: page.description,
  url: `https://njordheim.com${page.path}`,
  isPartOf: {
    '@type': 'WebSite',
    name: 'Njordheim Arena',
    url: 'https://njordheim.com/'
  }
});

const PublicDiscoveryPage: React.FC<PublicDiscoveryPageProps> = ({ page }) => {
  const relatedPages = PUBLIC_DISCOVERY_PAGES.filter((entry) => entry.path !== page.path);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page.path]);

  return (
    <div className={styles.page}>
      <SeoMeta
        title={page.metaTitle}
        description={page.description}
        path={page.path}
        jsonLd={[buildWebPageSchema(page), buildFaqSchema(page)]}
      />

      <main className={styles.shell}>
        <header className={styles.hero}>
          <Link to="/" className={styles.brandLink}>Njordheim Arena</Link>
          <p className={styles.eyebrow}>{page.eyebrow}</p>
          <h1 className={styles.heroTitle}>{page.heroTitle}</h1>
          <p className={styles.heroLead}>{page.lead}</p>

          <div className={styles.ctaRow}>
            <Link to="/register" className={styles.primaryCta}>Create Account</Link>
            <Link to="/" className={styles.secondaryCta}>Back to Home</Link>
            <a
              href="https://discord.gg/ZR3HDBX29C"
              target="_blank"
              rel="noreferrer"
              className={styles.tertiaryCta}
            >
              Join Discord
            </a>
          </div>
        </header>

        <section className={styles.highlightGrid}>
          {page.highlights.map((highlight) => (
            <div key={highlight} className={styles.highlightCard}>
              {highlight}
            </div>
          ))}
        </section>

        <section className={styles.sectionGrid}>
          {page.sections.map((section) => (
            <article key={section.heading} className={styles.sectionCard}>
              <h2>{section.heading}</h2>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
              {section.bullets && (
                <ul className={styles.bulletList}>
                  {section.bullets.map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              )}
            </article>
          ))}
        </section>

        <section className={styles.relatedSection}>
          <div className={styles.sectionHeader}>
            <p className={styles.sectionEyebrow}>Related Pages</p>
            <h2>Explore other search-focused guides</h2>
          </div>
          <div className={styles.relatedGrid}>
            {relatedPages.map((relatedPage) => (
              <article key={relatedPage.path} className={styles.relatedCard}>
                <h3>{relatedPage.cardTitle}</h3>
                <p>{relatedPage.cardDescription}</p>
                <Link to={relatedPage.path} className={styles.relatedLink}>Read this page</Link>
              </article>
            ))}
          </div>
        </section>

        <section className={styles.faqSection}>
          <div className={styles.sectionHeader}>
            <p className={styles.sectionEyebrow}>FAQ</p>
            <h2>Quick answers for this search topic</h2>
          </div>
          <div className={styles.faqGrid}>
            {page.faq.map((item) => (
              <article key={item.question} className={styles.faqCard}>
                <h3>{item.question}</h3>
                <p>{item.answer}</p>
              </article>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default PublicDiscoveryPage;